import { useState, useCallback } from 'react';
import { useToast } from '../contexts/ToastContext';

/**
 * Shared hook for Assignment/Event Details Modal save logic
 * 
 * Architecture:
 *   - applyLocalUpdate: UI-only — updates the modal's displayed entity (e.g. setSelectedAssignment)
 *   - apiUpdate: makes the single API call (e.g. AssignmentApi.updateAssignment)
 *   - onSaved: called AFTER API success — parent refreshes DataContext cache + app-wide data
 * 
 * This ensures exactly ONE API call per save, with immediate modal UI updates
 * and app-wide propagation after the server confirms.
 * 
 * @param {Object} options
 * @param {Object} options.entity - Current entity object (assignment or event)
 * @param {Function} options.buildPatch - (editedData, entity) => full merged payload for API
 * @param {Function} options.apiUpdate - async (id, payload) => savedEntity | null - Must throw on failure
 * @param {Function} options.applyLocalUpdate - (entityOrPatch) => void - Updates modal display only (NO API calls)
 * @param {Function} [options.onSaved] - async (serverData, id) => void - Called after API success to refresh app-wide data
 * @param {string} [options.entityType] - Type of entity ('event' or 'assignment') for success messages
 * @param {Function} [options.onSuccess] - Optional callback after successful save (clears editing state)
 * @param {Function} [options.onError] - Optional callback on error with error object
 * @returns {{ handleSave: Function, saving: boolean, error: string | null }}
 */
export function useDetailsModalSave({
  entity,
  buildPatch,
  apiUpdate,
  applyLocalUpdate,
  onSaved,
  entityType,
  onSuccess,
  onError
}) {
  const { addToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Resolve entity ID from various possible field names
   * Returns null if ID cannot be resolved to a valid number
   */
  const resolveEntityId = useCallback((entity) => {
    const raw = entity?.id ?? entity?.assignmentId ?? entity?.eventId;
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  }, []);

  /**
   * Main save handler — single API call with optimistic UI update and rollback
   */
  const handleSave = useCallback(async (editedData) => {
    // Validate ID before attempting save
    const id = resolveEntityId(entity);
    if (!id) {
      const errorMsg = 'Cannot save: missing or invalid ID';
      console.error('[useDetailsModalSave]', errorMsg, { entity });
      setError(errorMsg);
      onError?.(new Error(errorMsg));
      return;
    }

    // Build full payload with edited fields merged
    const payload = buildPatch(editedData, entity);

    // Store snapshot for rollback
    const beforeSnapshot = { ...entity };
    
    // Optimistic update: apply immediately to modal UI only
    const optimisticPatch = { ...beforeSnapshot, ...payload };
    applyLocalUpdate(optimisticPatch);

    setSaving(true);
    setError(null);

    try {
      // Single API call — the ONLY network request for this save
      const saved = await apiUpdate(id, payload);

      // Apply authoritative server response to modal (or keep optimistic if 204)
      const authoritative = saved ?? optimisticPatch;
      applyLocalUpdate(authoritative);

      setSaving(false);
      
      // Show entity-specific success toast
      const entityTypeName = entityType === 'event' ? 'Event' : entityType === 'assignment' ? 'Assignment' : 'Changes';
      addToast({
        message: `${entityTypeName} saved successfully`,
        intent: 'success',
        duration: 2000
      });
      
      onSuccess?.();

      // Notify parent to refresh app-wide data (DataContext cache, lists, etc.)
      // This runs AFTER the modal UI is already updated, so the user sees instant feedback.
      try {
        await onSaved?.(authoritative, id);
      } catch (refreshErr) {
        // Cache refresh failure is non-fatal — the API save already succeeded
        console.warn('[useDetailsModalSave] Post-save refresh failed (non-fatal):', refreshErr);
      }
    } catch (err) {
      // Rollback to original state on ANY error
      console.error('[useDetailsModalSave] Save failed, rolling back:', err);
      applyLocalUpdate(beforeSnapshot);

      const errorMsg = err.message || 'Failed to save changes';
      setError(errorMsg);
      setSaving(false);
      onError?.(err);
    }
  }, [entity, buildPatch, apiUpdate, applyLocalUpdate, onSaved, onSuccess, onError, resolveEntityId, addToast, entityType]);

  return {
    handleSave,
    saving,
    error
  };
}
